import { actionTypes } from '../../constants/actionTypes';
import { applyCustomFieldsAction, type SetCustomFieldsValuesAction } from '../customFieldsState';
import { taxonomyActionTypes } from '../../constants/taxonomyActionTypes';
import { isStructuralKrOperation } from '../../types/keywordRelevance';
import { extractAllElementIds, extractElementIdsFromHtml, htmlToJson, jsonToHtml } from '../common';
import { revertKrElementJsonChildren } from '../kr-structural-revert';
import { isGutenbergBlockContent } from '../gutenberg-utils';
import { initialState } from './store';

interface ReducerAction {
  type: string;
  [index: string]: any;
}

/**
 * Find the node whose `attributes.element_id` matches `elementId` and
 * return a new JSON tree with that node's children reverted to the
 * original text (and the `data-kr-rewrite-id` attribute stripped). Used
 * by the REJECT_KR_REWRITE reducer to keep pageDraftHtmlContent in sync
 * with the user's revert action so the Accept flow publishes the
 * reverted state. A leading <strong>/<b> label or single inline wrapper
 * survives the revert — the report's originalText is plain text, so
 * flattening would destroy formatting the rewrite preserved.
 *
 * Returns the same reference when no match is found, so callers can
 * short-circuit without re-serializing.
 */
function revertKrRewriteInJson(root: any, elementId: string, originalText: string): any {
  if (!root || typeof root !== 'object') return root;

  const attrId = root?.attributes?.element_id;
  if (attrId != null && String(attrId) === String(elementId)) {
    const nextAttrs = { ...(root.attributes || {}) };
    delete nextAttrs['data-kr-rewrite-id'];
    delete nextAttrs['data-kr-rewrite-reverted'];
    // For IMG, the "text" is actually the alt attribute. For everything
    // else we revert the children to the original text string.
    const tag = String(root.tag || '').toUpperCase();
    if (tag === 'IMG' || tag === 'IMAGE' || tag === 'IMG-ALT') {
      nextAttrs.alt = originalText || '';
      return { ...root, attributes: nextAttrs };
    }
    return {
      ...root,
      attributes: nextAttrs,
      children: revertKrElementJsonChildren(root, originalText),
    };
  }

  const children = Array.isArray(root.children) ? root.children : null;
  if (!children) return root;

  let mutated = false;
  const nextChildren = children.map((child: any) => {
    if (typeof child !== 'object' || child == null) return child;
    const updated = revertKrRewriteInJson(child, elementId, originalText);
    if (updated !== child) mutated = true;
    return updated;
  });
  if (!mutated) return root;
  return { ...root, children: nextChildren };
}

function removeKrRewriteInJson(root: any, elementId: string): any {
  if (!root || typeof root !== 'object') return root;

  const attrId = root?.attributes?.element_id;
  if (attrId != null && String(attrId) === String(elementId)) {
    return null;
  }

  const children = Array.isArray(root.children) ? root.children : null;
  if (!children) return root;

  let mutated = false;
  const nextChildren = children
    .map((child: any) => {
      if (typeof child !== 'object' || child == null) return child;
      const updated = removeKrRewriteInJson(child, elementId);
      if (updated !== child) mutated = true;
      return updated;
    })
    .filter((child: any) => child !== null);
  if (!mutated) return root;
  return { ...root, children: nextChildren };
}

function revertKrBodySpanInJson(root: any, elementIds: Array<string | number>, originalTexts: string[]): any {
  if (!root || typeof root !== 'object') return root;
  const originalsById = new Map<string, string>();
  elementIds.forEach((id, idx) => originalsById.set(String(id), originalTexts[idx] || ''));

  const attrId = root?.attributes?.element_id;
  if (attrId != null && originalsById.has(String(attrId))) {
    const nextAttrs = { ...(root.attributes || {}) };
    delete nextAttrs['data-kr-rewrite-id'];
    delete nextAttrs['data-kr-rewrite-reverted'];
    return {
      ...root,
      attributes: nextAttrs,
      children: revertKrElementJsonChildren(root, originalsById.get(String(attrId)) || ''),
    };
  }

  const children = Array.isArray(root.children) ? root.children : null;
  if (!children) return root;

  let mutated = false;
  const nextChildren = children.map((child: any) => {
    if (typeof child !== 'object' || child == null) return child;
    const updated = revertKrBodySpanInJson(child, elementIds, originalTexts);
    if (updated !== child) mutated = true;
    return updated;
  });
  if (!mutated) return root;
  return { ...root, children: nextChildren };
}

/**
 * Two taxonomy elicitation questions are "the same" when their ids match, or —
 * because ids are regenerated across hydrations/replayed inits — when their
 * trimmed prompt text matches. Empty text never matches by text.
 */
function isSameTaxonomyQuestion(a: any, b: any): boolean {
  if (!a || !b) return false;
  if (a.id && b.id && a.id === b.id) return true;
  const aText = typeof a.question === 'string' ? a.question.trim() : '';
  const bText = typeof b.question === 'string' ? b.question.trim() : '';
  return aText.length > 0 && aText === bText;
}

/**
 * Hydration heal for transcripts persisted before question dedupe existed:
 * a remount could replay the init stream and attach the same elicitation to a
 * second agent message, and that duplicate then got persisted to IndexedDB.
 * Keep one card per unique question — prefer an answered copy, else the first
 * occurrence — and strip the rest. Only the `question` attachment is removed;
 * message text and tool uses stay untouched.
 */
function dedupeHydratedTaxonomyQuestions(messages: any[]): any[] {
  if (!Array.isArray(messages)) return messages;

  const clusters: { index: number; question: any }[][] = [];
  messages.forEach((msg: any, index: number) => {
    if (!msg?.question) return;
    const cluster = clusters.find((members) => members.some((m) => isSameTaxonomyQuestion(m.question, msg.question)));
    if (cluster) cluster.push({ index, question: msg.question });
    else clusters.push([{ index, question: msg.question }]);
  });

  const dropIndexes = new Set<number>();
  for (const members of clusters) {
    if (members.length < 2) continue;
    const keeper = members.find((m) => m.question.answered) || members[0];
    for (const m of members) {
      if (m !== keeper) dropIndexes.add(m.index);
    }
  }
  if (dropIndexes.size === 0) return messages;

  return messages.map((msg: any, index: number) => {
    if (!dropIndexes.has(index)) return msg;
    const cleaned = { ...msg };
    delete cleaned.question;
    return cleaned;
  });
}

/**
 * Reconcile tools left mid-flight. A turn ends `running` two ways that never
 * deliver a completion chunk: the user stops it, or the transcript is
 * rehydrated after the stream died with the tab. Either way the work is not
 * happening, so a spinner and a live elapsed ticker assert something untrue.
 *
 * Safe on hydration because nothing streams while a session loads; should a
 * live `running` chunk arrive for the same tool id afterwards, the merge in
 * UPDATE_TAXONOMY_MESSAGE flips it straight back.
 *
 * `stampCompletion` is the difference between the two callers. A live cancel
 * happens NOW, so now is the honest stop time. Hydration has no idea when the
 * stream actually died — stamping it on load measures how long ago the tool
 * STARTED, which rendered a ten-minute run as "43m 17s". With no stamp the
 * duration is simply omitted, which is what we actually know.
 */
function cancelRunningTaxonomyTools(messages: any[], stampCompletion: boolean): any[] {
  if (!Array.isArray(messages)) return messages;
  const stoppedAt = Date.now();
  let healed = false;
  const next = messages.map((msg: any) => {
    if (!Array.isArray(msg?.toolUses)) return msg;
    if (!msg.toolUses.some((t: any) => t?.status === 'running')) return msg;
    healed = true;
    return {
      ...msg,
      toolUses: msg.toolUses.map((t: any) =>
        t?.status === 'running'
          ? { ...t, status: 'cancelled', ...(stampCompletion ? { completedAt: stoppedAt } : {}) }
          : t
      ),
    };
  });
  // Hand back the SAME array when nothing was stranded. Hydration runs this
  // on every transcript load, and a fresh array would re-render the whole
  // message list for no reason.
  return healed ? next : messages;
}

export const reducer = (state = initialState, action: ReducerAction) => {
  switch (action.type) {
    case actionTypes.TOGGLE_SIDEBAR: {
      return {
        ...state,
        sidebarOpen: action.sidebarOpen,
      };
    }

    case actionTypes.SET_GUTENBERG_BLOCK_SETTINGS_SIDEBAR: {
      return {
        ...state,
        gutenbergBlockSettingsSidebar: action.isOpen,
      };
    }

    case actionTypes.SET_GUTENBERG_COMMENTS_SIDEBAR: {
      return {
        ...state,
        gutenbergCommentsSidebar: action.isOpen,
      };
    }

    case actionTypes.SET_TOPTRENDS_LIST: {
      if (JSON.stringify(state.topTrendsList) === JSON.stringify(action.topTrendsList)) {
        return state;
      }

      return {
        ...state,
        topTrendsList: action.topTrendsList,
      };
    }
    case actionTypes.MERGE_TOPTRENDS_LLM_INSIGHTS: {
      const insightsMap = new Map<any, any>(action.insights.map((item: any) => [item.id, item]));
      let changed = false;
      const merged = state.topTrendsList.map((trend: any) => {
        const fresh = insightsMap.get(trend.id);
        if (
          fresh &&
          (trend.llm_insight_text !== fresh.llm_insight_text ||
            trend.llm_highlight_keywords !== fresh.llm_highlight_keywords)
        ) {
          changed = true;
          return {
            ...trend,
            llm_insight_text: fresh.llm_insight_text,
            llm_highlight_keywords: fresh.llm_highlight_keywords,
          };
        }
        return trend;
      });
      return changed ? { ...state, topTrendsList: merged } : state;
    }
    case actionTypes.SET_FULL_SCREEN: {
      return {
        ...state,
        showFullScreen: action.showFullScreen,
        sidebarOpen: action.sidebarOpen,
      };
    }
    case actionTypes.SET_EXPLORES_DATA: {
      if (JSON.stringify(state.explores) === JSON.stringify(action.explores)) {
        return state;
      }

      return {
        ...state,
        explores: action.explores,
      };
    }
    case actionTypes.SET_LOOKS_LIST: {
      if (action.reset) {
        return {
          ...state,
          looksList: action.looksList,
        };
      } else {
        // dedupe entries in the list
        const looksMap = new Map();
        state.looksList.forEach((look) => looksMap.set(look.id, look));
        action.looksList?.forEach((look) => looksMap.set(look.id, look));

        const newLooksList = Array.from(looksMap.values()).sort();

        if (JSON.stringify(state.looksList) === JSON.stringify(newLooksList)) {
          return state;
        }

        return {
          ...state,
          looksList: newLooksList,
        };
      }
    }
    case actionTypes.SET_DASHBOARDS_LIST: {
      if (action.reset) {
        return {
          ...state,
          dashboardsList: action.dashboardsList,
        };
      }

      // Deduped merge — never append-only, so shared-folder fetches cannot
      // duplicate personal/embed dashboards that My Analysis also renders.
      const dashboardsMap = new Map();
      state.dashboardsList.forEach((dashboard) => dashboardsMap.set(dashboard.id, dashboard));
      action.dashboardsList?.forEach((dashboard) => dashboardsMap.set(dashboard.id, dashboard));

      const newDashboardsList = Array.from(dashboardsMap.values()).sort();

      if (JSON.stringify(state.dashboardsList) === JSON.stringify(newDashboardsList)) {
        return state;
      }

      return {
        ...state,
        dashboardsList: newDashboardsList,
      };
    }
    case actionTypes.SET_BLOB_URL: {
      return {
        ...state,
        blobUrls: { ...state.blobUrls, [action.id]: action.url },
      };
    }
    case actionTypes.SET_LOOKER_PERMISSIONS: {
      if (JSON.stringify(state.looker_permissions) === JSON.stringify(action.looker_permissions)) {
        return state;
      }

      return {
        ...state,
        looker_permissions: action.looker_permissions,
      };
    }
    case actionTypes.SET_SNACKBAR: {
      if (JSON.stringify(state.snackConfig) === JSON.stringify(action.snackConfig)) {
        return state;
      }

      return {
        ...state,
        snackConfig: { ...action.snackConfig },
      };
    }
    case actionTypes.SET_CONFIRMATION_MODAL: {
      if (JSON.stringify(state.confirmationModal) === JSON.stringify(action.modalConfig)) {
        return state;
      }

      return {
        ...state,
        confirmationModal: { ...action.modalConfig },
      };
    }
    case actionTypes.SET_ADHOC_ANALYSIS_FOLDERS: {
      if (JSON.stringify(state.adHocAnalysisFolders) === JSON.stringify(action.adHocAnalysisFolders)) {
        return state;
      }

      return {
        ...state,
        adHocAnalysisFolders: action.adHocAnalysisFolders,
      };
    }
    case actionTypes.SET_PERSONAL_FOLDERS: {
      if (
        JSON.stringify(state.personalFolders) === JSON.stringify(action.personalFolders) &&
        state.personalFolderId === action.personalFolderId
      ) {
        return state;
      }

      return {
        ...state,
        personalFolders: action.personalFolders || [],
        personalFolderId: action.personalFolderId ?? state.personalFolderId,
      };
    }
    case actionTypes.SET_SEGMENT_FILTERS_DATA: {
      if (JSON.stringify(state.segmentFiltersData) === JSON.stringify(action.segmentFiltersData)) {
        return state;
      }

      return {
        ...state,
        segmentFiltersData: { ...action.segmentFiltersData },
      };
    }
    case actionTypes.SET_SUMMARY_METRIC_DATA: {
      if (JSON.stringify(state.summaryMetricData) === JSON.stringify(action.summaryMetricData)) {
        return state;
      }

      return {
        ...state,
        summaryMetricData: { ...action.summaryMetricData },
      };
    }
    case actionTypes.SET_DRILLDOWN_METRIC_DATA: {
      if (JSON.stringify(state.drilldownCachedMetricData) === JSON.stringify(action.drilldownCachedMetricData)) {
        return state;
      }

      return {
        ...state,
        drilldownCachedMetricData: { ...action.drilldownCachedMetricData },
      };
    }
    case actionTypes.SET_SLACK_CHANNELS: {
      if (JSON.stringify(state.slackChannels) === JSON.stringify(action.slackChannels)) {
        return state;
      }

      return {
        ...state,
        slackChannels: [...action.slackChannels],
      };
    }
    case actionTypes.ADD_LOADER: {
      if (!action.loaderName) {
        return state;
      }

      return {
        ...state,
        loadersArray: [...state.loadersArray, action.loaderName],
      };
    }
    case actionTypes.REMOVE_LOADER: {
      if (!action.loaderName) {
        return state;
      }

      const currentLoaderArray = [...state.loadersArray];
      currentLoaderArray.splice(currentLoaderArray.indexOf(action.loaderName), 1);
      return {
        ...state,
        loadersArray: [...currentLoaderArray],
      };
    }
    case actionTypes.SET_COMPETITORS_DATA: {
      if (JSON.stringify(state.competitorsData) === JSON.stringify(action.competitorsData)) {
        return state;
      }

      return {
        ...state,
        competitorsData: action.competitorsData,
      };
    }
    case actionTypes.SET_TOP_N_DATA: {
      if (JSON.stringify(state.topNData) === JSON.stringify(action.topNData)) {
        return state;
      }

      return {
        ...state,
        topNData: action.topNData,
      };
    }
    case actionTypes.SET_DRILL_DOWN_ROW_DATA: {
      if (JSON.stringify(state.drillDownRowData) === JSON.stringify(action.drillDownRowData)) {
        return state;
      }

      return {
        ...state,
        drillDownRowData: action.drillDownRowData,
      };
    }
    case actionTypes.UPDATE_GT_SELECTED_FILTERS: {
      return {
        ...state,
        gtSelectedFilters: { ...state.gtSelectedFilters, ...action.gtSelectedFilters },
      };
    }
    case actionTypes.UPDATE_ST_SELECTED_FILTERS: {
      return {
        ...state,
        stSelectedFilters: { ...state.stSelectedFilters, ...action.stSelectedFilters },
      };
    }
    case actionTypes.SET_HEADER_INFO: {
      if (JSON.stringify(state.headerInfo) === JSON.stringify(action.payload)) {
        return state;
      }

      return {
        ...state,
        headerInfo: {
          ...action.payload,
        },
      };
    }
    case actionTypes.UNSET_HEADER_INFO: {
      return {
        ...state,
        headerInfo: {
          title: '',
        },
      };
    }
    case actionTypes.SET_POTENTIAL_OPPORTUNITY_LIST: {
      if (JSON.stringify(state.potentialOpportunityList) === JSON.stringify(action.potentialOpportunityList)) {
        return state;
      }

      return {
        ...state,
        potentialOpportunityList: action.potentialOpportunityList,
      };
    }
    case actionTypes.SET_TOP_OPPORTUNITY_LIST: {
      if (JSON.stringify(state.topOpportunityList) === JSON.stringify(action.topOpportunityList)) {
        return state;
      }

      return {
        ...state,
        topOpportunityList: action.topOpportunityList,
      };
    }
    case actionTypes.SET_GROWTH_TREND_MARKET_SHARE_FILTERS: {
      if (JSON.stringify(state.growthTrendsMarketShareFilters) === JSON.stringify(action.filtersData)) {
        return state;
      }

      return {
        ...state,
        growthTrendsMarketShareFilters: {
          ...action.filtersData,
        },
      };
    }
    case actionTypes.SET_USABILITY_RECOMMENDATION_LIST: {
      if (JSON.stringify(state.usabilityRecommendationList) === JSON.stringify(action.usabilityRecommendationList)) {
        return state;
      }

      return {
        ...state,
        usabilityRecommendationList: action.usabilityRecommendationList,
      };
    }
    case actionTypes.SET_USABILITY_EXPORT_FILTERS: {
      return {
        ...state,
        usabilityExportFilters: action.filters,
      };
    }
    case actionTypes.SET_CONTENT_RECOMMENDATION_DATA: {
      if (JSON.stringify(state.contentRecommendationList) === JSON.stringify(action.contentRecommendationList)) {
        return state;
      }

      return {
        ...state,
        contentRecommendationList: action.contentRecommendationList,
      };
    }
    case actionTypes.SET_CONTENT_BRIEF_DATA: {
      if (JSON.stringify(state.contentBriefList) === JSON.stringify(action.contentBriefList)) {
        return state;
      }

      return {
        ...state,
        contentBriefList: action.contentBriefList,
      };
    }
    case actionTypes.SET_PAGES_UPLOADS: {
      if (JSON.stringify(state.customerPagesUploads) === JSON.stringify(action.customerPagesUploads)) {
        return state;
      }

      return {
        ...state,
        customerPagesUploads: action.customerPagesUploads,
      };
    }
    case actionTypes.SET_KEYWORDS_UPLOADS: {
      if (JSON.stringify(state.customerKeywordsUploads) === JSON.stringify(action.customerKeywordsUploads)) {
        return state;
      }

      return {
        ...state,
        customerKeywordsUploads: action.customerKeywordsUploads,
      };
    }
    case actionTypes.SET_PRIMARY_SEGMENT_NAME: {
      if (JSON.stringify(state.segment1_name) === JSON.stringify(action.segment1_name)) {
        return state;
      }

      return {
        ...state,
        segment1_name: action.segment1_name,
      };
    }
    case actionTypes.SET_SECONDARY_SEGMENT_NAME: {
      if (JSON.stringify(state.segment2_name) === JSON.stringify(action.segment2_name)) {
        return state;
      }

      return {
        ...state,
        segment2_name: action.segment2_name,
      };
    }
    case actionTypes.SET_CUSTOMER_INFO: {
      // to prevent rerender when customer info is updated from other components
      if (JSON.stringify(state.customerInfo) === JSON.stringify(action.customerInfo)) {
        return state;
      }
      return {
        ...state,
        customerInfo: action.customerInfo,
      };
    }
    case actionTypes.SET_GLOBAL_DATE_RANGE_FILTERS: {
      if (JSON.stringify(state.datePickerFilter) === JSON.stringify(action.filtersData)) {
        return state;
      }

      return {
        ...state,
        datePickerFilter: action.filtersData,
      };
    }
    case actionTypes.SET_USER_SELECTED_DATE_RANGE: {
      return {
        ...state,
        userSelectedDateRange: action.userSelectedDateRange,
      };
    }
    case actionTypes.SET_DEFAULT_DATE_RANGE: {
      if (JSON.stringify(state.defaultDateRange) === JSON.stringify(action.defaultDateRange)) {
        return state;
      }

      return {
        ...state,
        defaultDateRange: action.defaultDateRange,
      };
    }
    case actionTypes.SET_RECOMMENDATION_FILTERS_DATA: {
      if (JSON.stringify(state.recommendationFiltersData) === JSON.stringify(action.filtersData)) {
        return state;
      }

      return {
        ...state,
        recommendationFiltersData: action.filtersData,
      };
    }
    // Page options are refreshed on every filter change, independently of the
    // one-shot bulk filters load, so they merge instead of replacing the blob.
    case actionTypes.SET_RECOMMENDATION_PAGE_FILTER_OPTIONS: {
      const current = (state.recommendationFiltersData || {}) as Record<string, unknown>;
      if (JSON.stringify(current.Page) === JSON.stringify(action.pageOptions)) {
        return state;
      }

      return {
        ...state,
        recommendationFiltersData: {
          ...current,
          Page: action.pageOptions,
        },
      };
    }
    case actionTypes.SET_CONTENT_RECOMMENDATION_FILTERS_DATA: {
      return {
        ...state,
        contentRecommendationFiltersData: { ...state.contentRecommendationFiltersData, ...action.filtersData },
      };
    }
    case actionTypes.SET_USABILITY_METRICS_SCORE: {
      if (JSON.stringify(state.usabilityScore) === JSON.stringify(action.usabilityScore)) {
        return state;
      }

      return {
        ...state,
        usabilityScore: action.usabilityScore,
      };
    }
    case actionTypes.SET_DOMAIN: {
      if (state.defaultDomain === action.defaultDomain) {
        return state;
      }

      if (action.defaultDomain === '/') {
        return state;
      }

      return {
        ...state,
        defaultDomain: action.defaultDomain,
      };
    }
    case actionTypes.SET_MARKET_SEGMENT: {
      if (JSON.stringify(state.defaultMarketSegment) === JSON.stringify(action.defaultMarketSegment)) {
        return state;
      }

      return {
        ...state,
        defaultMarketSegment: action.defaultMarketSegment,
      };
    }
    case actionTypes.SET_CR_DETAILS_MODAL: {
      if (JSON.stringify(state.crDetailsModal) === JSON.stringify(action.crDetailsModal)) {
        return state;
      }

      return {
        ...state,
        crDetailsModal: action.crDetailsModal,
      };
    }
    case actionTypes.ADD_PAGE_TO_OPTIMIZE_FORM_BOX: {
      if (
        JSON.stringify(state?.addPageToOptimizeModelForTrends) ===
        JSON.stringify(action.addPageToOptimizeModelForTrends)
      ) {
        return state;
      }
      return {
        ...state,
        addPageToOptimizeModelForTrends: action.addPageToOptimizeModelForTrends,
      };
    }
    case actionTypes.SET_LOOKER_USER_ID: {
      if (JSON.stringify(state.lookerUserId) === JSON.stringify(action.lookerUserId)) {
        return state;
      }

      return {
        ...state,
        lookerUserId: action.lookerUserId,
      };
    }
    case actionTypes.SET_INTENT_LIST: {
      if (JSON.stringify(state.intentList) === JSON.stringify(action.intentList)) {
        return state;
      }

      return {
        ...state,
        intentList: action.intentList,
      };
    }
    case actionTypes.SET_RULES_LIST: {
      if (JSON.stringify(state.ruleList) === JSON.stringify(action.ruleList)) {
        return state;
      }

      return {
        ...state,
        ruleList: action.ruleList,
      };
    }
    case actionTypes.SET_ENTITY_LIST: {
      if (JSON.stringify(state.entityList) === JSON.stringify(action.entityList)) {
        return state;
      }

      return {
        ...state,
        entityList: action.entityList,
      };
    }
    case actionTypes.STORE_RULES_LIST: {
      if (JSON.stringify(state.ruleStoreList) === JSON.stringify(action.ruleStoreList)) {
        return state;
      }

      return {
        ...state,
        ruleStoreList: action.ruleStoreList,
      };
    }
    case actionTypes.SET_ALL_SEARCH_INTENT_LIST: {
      if (JSON.stringify(state.intentAllSearchList) === JSON.stringify(action.intentAllSearchList)) {
        return state;
      }

      return {
        ...state,
        intentAllSearchList: action.intentAllSearchList,
      };
    }
    case actionTypes.SET_ELEMENT_MAPPING_DETAILS: {
      if (JSON.stringify(state.elementMappingDetails) === JSON.stringify(action.elementMappingDetails)) {
        return state;
      }

      return {
        ...state,
        elementMappingDetails: action.elementMappingDetails,
      };
    }
    case actionTypes.SET_ELEMENT_MAPPING_DETAILS_COMPETITOR: {
      return {
        ...state,
        elementMappingDetailsCompetitor: action.elementMappingDetails,
      };
    }
    case actionTypes.UPDATE_CONTENT_QUALITY_SCORE: {
      const newState = { ...state };
      if (newState.contentRecommendationList.length > 0) {
        const updatedList = newState.contentRecommendationList.map((item) => {
          if (action.payload.url === item.finalurl) {
            item.projectedContentQualityScore = action.payload.newProjectedScore;
          }
          return item;
        });
        newState.contentRecommendationList = [...updatedList];
      }
      return newState;
    }
    // case actionTypes.SET_CUSTOMER_PREVIEW_HTML:
    //   return {
    //     ...state,
    //     customerPreviewHtml: action.htmlContent,
    //   };
    case actionTypes.SET_INTENT_SUMMARY: {
      if (JSON.stringify(state.intentSummaryData) === JSON.stringify(action.intentSummaryData)) {
        return state;
      }

      return {
        ...state,
        intentSummaryData: action.intentSummaryData,
      };
    }
    case actionTypes.SET_ALL_SEARCH_INTENT_SUMMARY: {
      if (JSON.stringify(state.intentAllSearchSummaryData) === JSON.stringify(action.intentAllSearchSummaryData)) {
        return state;
      }

      return {
        ...state,
        intentAllSearchSummaryData: action.intentAllSearchSummaryData,
      };
    }
    case actionTypes.SET_CATEGORY_LIST: {
      if (JSON.stringify(state.categoryList) === JSON.stringify(action.categoryList)) {
        return state;
      }

      return {
        ...state,
        categoryList: action.categoryList,
      };
    }

    case actionTypes.SET_CATEGORY_PAGE_LIST: {
      if (JSON.stringify(state.categoryPageList) === JSON.stringify(action.categoryPageList)) {
        return state;
      }

      return {
        ...state,
        categoryPageList: action.categoryPageList,
      };
    }

    case actionTypes.SET_CATEGORY_SUMMARY: {
      if (JSON.stringify(state.categorySummaryData) === JSON.stringify(action.categorySummaryData)) {
        return state;
      }

      return {
        ...state,
        categorySummaryData: action.categorySummaryData,
      };
    }
    case actionTypes.SET_ALL_SEARCH_CATEGORY_LIST: {
      if (JSON.stringify(state.categoryAllSearchList) === JSON.stringify(action.categoryAllSearchList)) {
        return state;
      }

      return {
        ...state,
        categoryAllSearchList: action.categoryAllSearchList,
      };
    }
    case actionTypes.SET_ALL_SEARCH_CATEGORY_SUMMARY: {
      if (JSON.stringify(state.categoryAllSearchSummaryData) === JSON.stringify(action.categoryAllSearchSummaryData)) {
        return state;
      }

      return {
        ...state,
        categoryAllSearchSummaryData: action.categoryAllSearchSummaryData,
      };
    }

    case actionTypes.CR_FULL_SCREEN_SPINNER: {
      if (JSON.stringify(state.showCrFullScreenSpinner) === JSON.stringify(action.showCrFullScreenSpinner)) {
        return state;
      }

      return {
        ...state,
        showCrFullScreenSpinner: action.showCrFullScreenSpinner,
      };
    }
    case actionTypes.CR_PAGE_TAGS_HTML_DATA_COLLECTION: {
      if (JSON.stringify(state.crPageTagsHtmlDataCollection) === JSON.stringify(action.crPageTagsHtmlDataCollection)) {
        return state;
      }

      return {
        ...state,
        crPageTagsHtmlDataCollection: action.crPageTagsHtmlDataCollection,
      };
    }
    case actionTypes.ALL_COMPETITORS_HTML_DATA: {
      if (JSON.stringify(state.allCompetitorsHtmlData) === JSON.stringify(action.allCompetitorsHtmlData)) {
        return state;
      }

      return {
        ...state,
        allCompetitorsHtmlData: action.allCompetitorsHtmlData,
      };
    }

    case actionTypes.PAGE_ALL_HTML_DATA: {
      if (JSON.stringify(state.pageAllHtmlData) === JSON.stringify(action.pageAllHtmlData)) {
        return state;
      }

      return {
        ...state,
        pageAllHtmlData: action.pageAllHtmlData,
      };
    }
    case actionTypes.TOPICS_DATA: {
      if (JSON.stringify(state.topicsData) === JSON.stringify(action.topicsData)) {
        return state;
      }

      return {
        ...state,
        topicsData: action.topicsData,
      };
    }

    case actionTypes.KEYWORDS_DATA: {
      if (JSON.stringify(state.keywordsData) === JSON.stringify(action.keywordsData)) {
        return state;
      }
      const cikKeywordsData = [...action.keywordsData]?.filter((ckd) => ckd?.is_cluster_included);
      const combinedKeywordsData = combineKeywordsData(
        cikKeywordsData,
        state.potentialKeywordsData,
        state.aiKeywordsData
      );
      return {
        ...state,
        cikKeywordsData: cikKeywordsData,
        keywordsData: combinedKeywordsData,
      };
    }

    case actionTypes.POTENTIAL_KEYWORDS_DATA: {
      if (JSON.stringify(state.potentialKeywordsData) === JSON.stringify(action.potentialKeywordsData)) {
        return state;
      }
      const combinedKeywordsData = combineKeywordsData(
        state.cikKeywordsData,
        action.potentialKeywordsData,
        state.aiKeywordsData
      );

      return {
        ...state,
        potentialKeywordsData: action.potentialKeywordsData,
        keywordsData: combinedKeywordsData,
      };
    }
    case actionTypes.AI_KEYWORDS_DATA: {
      if (JSON.stringify(state.aiKeywordsData) === JSON.stringify(action.aiKeywordsData)) {
        return state;
      }
      const combinedKeywordsData = combineKeywordsData(
        state.cikKeywordsData,
        state.potentialKeywordsData,
        action.aiKeywordsData
      );

      return {
        ...state,
        aiKeywordsData: action.aiKeywordsData,
        keywordsData: combinedKeywordsData,
      };
    }

    case actionTypes.RE_UPDATE_ALL_KEYWORDS_DATA: {
      const combinedKeywordsData = combineKeywordsData(
        state.cikKeywordsData,
        action.potentialKeywordsData,
        action.aiKeywordsData
      );
      return {
        ...state,
        potentialKeywordsData: action.potentialKeywordsData,
        aiKeywordsData: action.aiKeywordsData,
        keywordsData: combinedKeywordsData,
      };
    }

    case actionTypes.IS_INCLUDED_KEYWORDS_PIPELINE_TRIGGER: {
      if (
        JSON.stringify(state.isTriggerIncludedPotentialKeyword) ===
        JSON.stringify(action.isTriggerIncludedPotentialKeyword)
      ) {
        return state;
      }

      return {
        ...state,
        isTriggerIncludedPotentialKeyword: action.isTriggerIncludedPotentialKeyword,
      };
    }
    case actionTypes.NEW_KEYWORD_TO_POTENTIAL_KEYWORDS_DATA: {
      const newPotentialKeywordData = [...state.potentialKeywordsData];
      newPotentialKeywordData.push(...action.payload);
      return {
        ...state,
        potentialKeywordsData: newPotentialKeywordData,
      };
    }
    case actionTypes.MASTER_COUNTRY_AND_LANGUAGE: {
      if (JSON.stringify(state.masterLanguageAndCountry) === JSON.stringify(action.payload)) {
        return state;
      }

      return {
        ...state,
        masterLanguageAndCountry: action.payload,
      };
    }
    case actionTypes.SET_NEW_PAGE_CONTENT_RECOMMENDATION_DATA: {
      const crList = [...state.contentRecommendationList];
      crList.unshift(action.payload);
      return {
        ...state,
        contentRecommendationList: crList,
      };
    }

    case actionTypes.ADD_NEW_PAGE_IN_CR_FILTERS: {
      const contentRecommendationFiltersData = { ...state.contentRecommendationFiltersData };
      if (contentRecommendationFiltersData && contentRecommendationFiltersData.page) {
        contentRecommendationFiltersData.page.unshift({ top_data: action.page });
      } else {
        contentRecommendationFiltersData.page = [{ top_data: action.page }];
      }
      return {
        ...state,
        contentRecommendationFiltersData: contentRecommendationFiltersData,
      };
    }

    case actionTypes.GLOBAL_AUTO_WRITER_MAGIC_PIN: {
      const currentData = { ...state.globalAutoWriterMagicPin };
      return {
        ...state,
        globalAutoWriterMagicPin: {
          ...currentData,
          open: !!action?.payload?.open,
          defaultText: action?.payload?.defaultText || '',
        },
      };
    }

    case actionTypes.SET_CONTENT_BRIEF_TINYMCE_STRING: {
      if (JSON.stringify(state.contentBriefTinyMceData) === JSON.stringify(action?.payload)) {
        return state;
      }
      const contentBriefString = action?.payload?.contentBriefString || '';
      const isGutenbergContent = isGutenbergBlockContent(contentBriefString);
      const json = isGutenbergContent ? null : htmlToJson(contentBriefString);
      // Gutenberg content keeps its "Page Wise" order too: without it the Passages / Headings tabs
      // fell back to the rows' arrival order, which sent any element re-inserted by a rescore (an
      // edited takeaway, for one) to the bottom of the list.
      const orderdExtractedEleIds: any[] = isGutenbergContent
        ? extractElementIdsFromHtml(contentBriefString)
        : extractAllElementIds(json) || [];
      return {
        ...state,
        // contentBriefSting: action?.contents,
        contentBriefTinyMceData: {
          ...state?.contentBriefTinyMceData,
          initialLoad: action?.payload?.initialLoad,
          contentBriefString,
          orderdExtractedEleIds,
        },
      };
    }

    case actionTypes.SET_GPT_STREAMING_DATA: {
      if (!action?.gptStreamingData) {
        return state;
      }

      const oldData = { ...state.gptStreamingData };
      return {
        ...state,
        gptStreamingData: {
          ...oldData,
          ...action?.gptStreamingData,
        },
      };
    }

    case actionTypes.SET_GPT_COMPLETE_CONTENT: {
      return {
        ...state,
        gptCompleteContent: {
          content: action?.content ?? '',
          timestamp: action?.timestamp ?? Date.now(),
        },
      };
    }

    case actionTypes.SET_GPT_THINKING_STATE: {
      return {
        ...state,
        gptThinkingState: {
          isThinking: action?.thinking?.isThinking ?? false,
          status: action?.thinking?.status ?? '',
          message: action?.thinking?.message ?? '',
        },
      };
    }

    case actionTypes.SET_GPT_THINKING_CONTENT: {
      const currentContent = state.gptThinkingContent ?? '';
      const shouldAppend = action?.append !== false; // Default to true if not specified
      return {
        ...state,
        gptThinkingContent: shouldAppend
          ? currentContent + (action?.thinkingContent ?? '')
          : action?.thinkingContent ?? '',
      };
    }

    case actionTypes.SET_GPT_PERSISTED_THINKING_CONTENT: {
      return {
        ...state,
        gptPersistedThinkingContent: {
          content: action?.content ?? '',
          timestamp: action?.timestamp ?? Date.now(),
          promptInfo: {
            promptFor: action?.promptInfo?.promptFor ?? '',
            keyword: action?.promptInfo?.keyword ?? '',
            title: action?.promptInfo?.title ?? '',
          },
        },
      };
    }

    case actionTypes.SET_WEB_SOCKET_INSTANCE: {
      return {
        ...state,
        wsInstance: action?.wsInstance,
      };
    }

    case actionTypes.SET_SHOW_SUBSCRIPTION_BANNER: {
      return {
        ...state,
        showSubscriberBanner: action?.showSubscriberBanner,
      };
    }

    case actionTypes.SET_CUSTOM_POPUP_STATUS: {
      return {
        ...state,
        customPopupConfig: {
          showPopup: action?.customPopupConfig?.showPopup,
          text: action?.customPopupConfig?.text,
          anchorText: action?.customPopupConfig?.anchorText,
          anchorLink: action?.customPopupConfig?.anchorLink,
        },
      };
    }

    case actionTypes.SET_SUBSCRIBER_META: {
      return {
        ...state,
        subscriberMeta: action?.subscriberMeta,
      };
    }

    case actionTypes.SET_CREDITS_REMAINING: {
      return {
        ...state,
        creditsRemaining: action?.creditsRemaining,
      };
    }

    case actionTypes.SET_REMAINING_DAY_PASS_CREDITS: {
      return {
        ...state,
        dayPassCreditsRemaining: action?.dayPassCreditsRemaining,
      };
    }

    case actionTypes.SET_CREDITS_REQUIRED_PER_DAY: {
      return {
        ...state,
        creditsUsagePerDay: action?.creditsUsagePerDay,
      };
    }

    case actionTypes.SET_INTERNAL_LINKING_FILTERS: {
      return {
        ...state,
        internalLinkingFilters: action?.internalLinkingFilters,
      };
    }

    case actionTypes.SET_AUTONOMOUS_INTERNAL_LINKING_DETAILS: {
      return {
        ...state,
        autoNomousInternalLinkingDetails: action?.details,
      };
    }

    // Customer-configured WordPress custom fields (Banner Title / description, Key Takeaways, …).
    case actionTypes.SET_CUSTOM_FIELDS_CONFIG:
      return { ...state, customFieldsConfig: Array.isArray(action.value) ? action.value : [] };
    case actionTypes.SET_CUSTOM_FIELDS_CONFIG_FETCHED:
      return { ...state, customFieldsConfigFetched: !!action.value };
    case actionTypes.SET_CUSTOM_FIELDS_VALUES: {
      // User edits (`merge`), the restored Quattr draft and the WordPress post (`fromPost`) all feed
      // the same map; which one wins per key is decided in utility/customFieldsState.ts.
      return { ...state, ...applyCustomFieldsAction(state, action as unknown as SetCustomFieldsValuesAction) };
    }

    case actionTypes.SET_PAGE_HTML_CONTENT: {
      let html = action?.html || '';
      const isString = typeof html === 'string';
      const json = isString ? (html ? htmlToJson(html) : null) : html;
      if (json?.tag?.toUpperCase() === 'BODY') {
        json.attributes = {};
      }
      if (!isString) {
        html = jsonToHtml(json) || '';
      }
      const orderdExtractedEleIds: any[] = extractAllElementIds(json) || [];
      return {
        ...state,
        pageDraftHtmlContent: {
          html,
          json,
          orderdExtractedEleIds,
        },
      };
    }
    case actionTypes.SET_GIGA_DRAFT_CONTENT: {
      const html = action?.html;
      const isString = typeof html === 'string';
      const json = isString ? (html ? htmlToJson(html) : null) : html;
      if (json?.tag?.toUpperCase() === 'BODY') {
        json.attributes = {};
      }
      return {
        ...state,
        gigaDraftContent: {
          html: isString ? html : html ? jsonToHtml(html) : '',
          json: json,
        },
      };
    }
    case actionTypes.SET_TEMP_GIGA_REVIEW_CONTENT: {
      const html = action?.html;
      const isString = typeof html === 'string';
      const json = isString ? (html ? htmlToJson(html) : null) : html;
      if (json?.tag?.toUpperCase() === 'BODY') {
        json.attributes = {};
      }
      return {
        ...state,
        tempGigaReviewContent: {
          html: isString ? html : html ? jsonToHtml(html) : '',
          json: json,
          isTemp: action?.isTemp,
        },
      };
    }

    // GIGA conflict resolution snapshot and conflicts
    case actionTypes.SET_GIGA_BASE_SNAPSHOT: {
      const html = action?.html;
      const isString = typeof html === 'string';
      const json = isString ? (html ? htmlToJson(html) : null) : html;
      if (json?.tag?.toUpperCase() === 'BODY') {
        json.attributes = {};
      }
      return {
        ...state,
        gigaBaseSnapshot: {
          html: isString ? html : html ? jsonToHtml(html) : '',
          json: json,
          timestamp: action?.timestamp || Date.now(),
        },
      };
    }
    case actionTypes.CLEAR_GIGA_BASE_SNAPSHOT: {
      return {
        ...state,
        gigaBaseSnapshot: { html: '', json: {}, timestamp: 0 },
      };
    }
    case actionTypes.SET_GIGA_CONFLICTS: {
      return {
        ...state,
        gigaConflicts: Array.isArray(action?.conflicts) ? action.conflicts : [],
      };
    }

    // ---------------------------------------------------------------------
    // GIGA — Boost Keyword Relevance (KR)
    // ---------------------------------------------------------------------
    case actionTypes.CLEAR_GIGA_KEYWORD_BOOST_STATE: {
      return {
        ...state,
        gigaKeywordBoost: {
          gapAnalysis: null,
          elements: {},
          iterations: [],
          completion: null,
          baselineByKeyword: { customer: {}, competitorBest: {}, customerScoreBasis: 'unknown' },
        },
      };
    }
    case actionTypes.SET_GIGA_KEYWORD_BOOST_GAP_ANALYSIS: {
      const p = action?.payload || {};
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          gapAnalysis: {
            targetTagGroups: Array.isArray(p.targetTagGroups) ? p.targetTagGroups : [],
            perTagGroup: p.perTagGroup || {},
            baselineProjectedKas: Number(p.baselineProjectedKas) || 0,
            timestamp: Date.now(),
          },
        },
      };
    }
    case actionTypes.ADD_GIGA_KEYWORD_BOOST_ELEMENT: {
      const p = action?.payload || {};
      const id = String(p.elementId);
      if (!id || id === 'undefined') return state;
      const existing = state.gigaKeywordBoost.elements?.[id];
      const incomingOperationType = p.operationType || 'element-rewrite';
      // Protect accepted structural rewrites from rejected CHILD-COLLISION
      // events (a rejected candidate sharing the structural rewrite's element
      // id) — but let authoritative backend demotions through: the service
      // marks final-slate hard demotions, net-negative warned-accept strips,
      // and mid-run structural guard reverts with finalDemotion so the UI
      // never keeps showing (or lets the user apply) a change the backend
      // has removed.
      if (
        existing?.accepted &&
        !existing.rejected &&
        !p.accepted &&
        !p.finalDemotion &&
        isStructuralKrOperation(existing.operationType)
      ) {
        return state;
      }
      const incomingBodySpanPayload = p.bodySpanPayload || null;
      const bodySpanPayload =
        existing?.operationType === 'body-span-rewrite' && incomingBodySpanPayload && existing.bodySpanPayload
          ? {
              ...incomingBodySpanPayload,
              originalTexts: Array.isArray(existing.bodySpanPayload.originalTexts)
                ? existing.bodySpanPayload.originalTexts
                : incomingBodySpanPayload.originalTexts,
            }
          : incomingBodySpanPayload;
      const coalescePerKeywordElementScores = (
        first?: Record<string, { before?: number; after?: number; delta?: number }>,
        last?: Record<string, { before?: number; after?: number; delta?: number }>
      ) => {
        const keywords = new Set([...Object.keys(first || {}), ...Object.keys(last || {})]);
        const out: Record<string, { before: number; after: number; delta: number }> = {};
        keywords.forEach((keyword) => {
          const before = Number(first?.[keyword]?.before);
          const after = Number(last?.[keyword]?.after);
          if (!Number.isFinite(before) || !Number.isFinite(after)) return;
          out[keyword] = {
            before,
            after,
            delta: Number((after - before).toFixed(3)),
          };
        });
        return Object.keys(out).length ? out : undefined;
      };
      const perKeywordElementScores = existing?.perKeywordElementScores
        ? p.perKeywordElementScores
          ? coalescePerKeywordElementScores(existing.perKeywordElementScores, p.perKeywordElementScores)
          : existing.perKeywordElementScores
        : p.perKeywordElementScores;
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          elements: {
            ...state.gigaKeywordBoost.elements,
            [id]: {
              elementId: p.elementId,
              tagGroup: p.tagGroup,
              elementTag: p.elementTag,
              iteration: Number(p.iteration) || 0,
              beforeScore: existing?.beforeScore != null ? existing.beforeScore : Number(p.beforeScore) || 0,
              afterScore: Number(p.afterScore) || 0,
              displayBeforeScore:
                existing?.displayBeforeScore != null
                  ? existing.displayBeforeScore
                  : Number(p.displayBeforeScore) || undefined,
              displayAfterScore: Number(p.displayAfterScore) || undefined,
              perKeywordElementScores,
              accepted: !!p.accepted,
              internalOnly: !!p.internalOnly,
              rejectionReason: p.rejectionReason,
              warning: p.warning,
              surfaced: !!p.surfaced,
              surfacedReason: p.surfacedReason,
              competitorBest: Number(p.competitorBest) || 0,
              original: existing?.original != null ? existing.original : p.originalSnippet || '',
              rewritten: p.rewrittenSnippet || '',
              rejected: false,
              timestamp: Date.now(),
              operationType: incomingOperationType,
              applicationMode: p.applicationMode,
              sectionPayload: p.sectionPayload || null,
              splitPayload: p.splitPayload || null,
              headingAddPayload: p.headingAddPayload || null,
              bodySpanPayload,
            },
          },
        },
      };
    }
    case actionTypes.UPDATE_GIGA_KEYWORD_BOOST_ITERATION: {
      const p = action?.payload || {};
      const entry = {
        iteration: Number(p.iteration) || 0,
        acceptedCount: Number(p.acceptedCount) || 0,
        rejectedCount: Number(p.rejectedCount) || 0,
        projectedKasBefore: Number(p.projectedKasBefore) || 0,
        projectedKasAfter: Number(p.projectedKasAfter) || 0,
        perTagGroupBeforeAfter: p.perTagGroupBeforeAfter || {},
        competitorGapRemaining: p.competitorGapRemaining || {},
        timestamp: Date.now(),
      };
      const existing = state.gigaKeywordBoost.iterations.filter((it) => it.iteration !== entry.iteration);
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          iterations: [...existing, entry].sort((a, b) => a.iteration - b.iteration),
        },
      };
    }
    case actionTypes.SET_GIGA_KEYWORD_BOOST_COMPLETE: {
      const p = action?.payload || {};
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          completion: {
            totalIterations: Number(p.totalIterations) || 0,
            finalProjectedKas: Number(p.finalProjectedKas) || 0,
            baselineProjectedKas: Number(p.baselineProjectedKas) || 0,
            success: !!p.success,
            stopReason: p.stopReason || 'max_iterations',
            outcomeStatus: p.outcomeStatus,
            rawOutcomeStatus: p.rawOutcomeStatus,
            officialOutcomeStatus: p.officialOutcomeStatus,
            dominantBlocker: p.dominantBlocker,
            outcomeEvidence: p.outcomeEvidence || [],
            rawKasDelta: Number(p.rawKasDelta) || 0,
            officialKasDelta: Number(p.officialKasDelta) || 0,
            rawKrRewriteLift: Number(p.rawKrRewriteLift) || 0,
            officialPipelineLift: Number(p.officialPipelineLift) || 0,
            krAttributedLift: Number(p.krAttributedLift) || 0,
            meaningfulLift: !!p.meaningfulLift,
            acceptedButNoOfficialLift: !!p.acceptedButNoOfficialLift,
            s3ReportKey: p.s3ReportKey,
            acceptedRewriteCount: Number(p.acceptedRewriteCount) || 0,
            acceptedVisibleRewriteCount: Number(p.acceptedVisibleRewriteCount) || 0,
            krAlgorithmVersion: p.krAlgorithmVersion,
            buildFingerprint: p.buildFingerprint || null,
            krDecisionPlan: p.krDecisionPlan || null,
            krIntentActionPlan: p.krIntentActionPlan || null,
            krHeadingOutlinePlan: p.krHeadingOutlinePlan || null,
            headingOutlineActions: p.headingOutlineActions || [],
            timestamp: Date.now(),
            perKeywordTagGroupBefore: p.perKeywordTagGroupBefore || {},
            perKeywordTagGroupAfter: p.perKeywordTagGroupAfter || {},
            comparablePerKeywordTagGroupBefore: p.comparablePerKeywordTagGroupBefore || {},
            comparablePerKeywordTagGroupAfter: p.comparablePerKeywordTagGroupAfter || {},
            perKeywordTagGroupCompetitor: p.perKeywordTagGroupCompetitor || {},
            perKeywordElementScoreBefore: p.perKeywordElementScoreBefore || {},
            perKeywordElementScoreAfter: p.perKeywordElementScoreAfter || {},
            perKeywordKesBefore: p.perKeywordKesBefore || {},
            perKeywordKesAfter: p.perKeywordKesAfter || {},
            comparablePerKeywordKesBefore: p.comparablePerKeywordKesBefore || {},
            comparablePerKeywordKesAfter: p.comparablePerKeywordKesAfter || {},
            perKeywordScoreProvenance: p.perKeywordScoreProvenance || null,
            competitorGapPlan: p.competitorGapPlan || null,
            inputFreshnessAudit: p.inputFreshnessAudit || p.input_freshness_audit || null,
            metadataFieldPolicy: p.metadataFieldPolicy || null,
            aggregateNoLiftRescue: p.aggregateNoLiftRescue || null,
            metadataFluencyIssues: p.metadataFluencyIssues || null,
            acceptedSlateQuality: p.acceptedSlateQuality || null,
            structurePreservationSummary: p.structurePreservationSummary || null,
            phraseSaturation: p.phraseSaturation || null,
            earlyNoLiftStop: p.earlyNoLiftStop || null,
            repetitionPreventedCount: Number(p.repetitionPreventedCount) || 0,
            opportunityCensus: p.opportunityCensus || null,
            advisory: p.advisory,
            optimizationEffort: p.optimizationEffort || null,
          },
        },
      };
    }
    case actionTypes.SET_GIGA_KEYWORD_BOOST_BASELINE_BY_KEYWORD: {
      const p = action?.payload || {};
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          baselineByKeyword: {
            customer: p.customer || {},
            competitorBest: p.competitorBest || {},
            customerScoreBasis: p.customerScoreBasis || 'unknown',
          },
        },
      };
    }
    case actionTypes.REJECT_KR_REWRITE: {
      const id = String(action?.elementId);
      const existing = state.gigaKeywordBoost.elements[id];
      if (!existing) return state;

      // ALSO revert the node in pageDraftHtmlContent.json AND
      // tempGigaReviewContent.json so every draft state the Accept flow may
      // persist matches the user's view. The inline highlighter mutates the
      // rendered DOM but the accept flow reads the S3 `giga_draft.json` —
      // without this, clicking Accept after reverting would publish the KR
      // rewrite anyway. tempGigaReviewContent is the GIGA review tab's
      // working copy (it also carries section deletions / link edits), so
      // patching it here is what lets one Accept include KR reverts and
      // section deletions together without them clobbering each other. We
      // find the node by element_id, replace its children with the original
      // text, and strip `data-kr-rewrite-id` so the UI no longer shows a
      // badge on it.
      const bodySpanPayload = existing.operationType === 'body-span-rewrite' ? existing.bodySpanPayload : null;
      // heading-add elements are ALWAYS inserted by KR — reverting one means
      // removing it. Detect by operation type alone: the backend report
      // stores the placeholder string 'No heading' (not an empty string) as
      // the original, so an empty-original heuristic replaces the heading
      // text with the literal words "No heading" instead of deleting it.
      const isInsertedHeading = existing.operationType === 'heading-add';
      const applyKrRevertToJson = (root: any): any => {
        if (!root) return root;
        if (bodySpanPayload) {
          return revertKrBodySpanInJson(root, bodySpanPayload.elementIds || [], bodySpanPayload.originalTexts || []);
        }
        if (isInsertedHeading) return removeKrRewriteInJson(root, id);
        if (existing.original == null) return root;
        return revertKrRewriteInJson(root, id, existing.original);
      };

      const draft = state.pageDraftHtmlContent;
      const draftJson = draft?.json;
      let nextDraft = draft;
      if (draftJson) {
        const revertedJson = applyKrRevertToJson(draftJson);
        if (revertedJson !== draftJson) {
          nextDraft = {
            ...(draft || {}),
            json: revertedJson,
            html: jsonToHtml(revertedJson) || '',
            orderdExtractedEleIds: extractAllElementIds(revertedJson) || [],
          };
        }
      }

      const temp = state.tempGigaReviewContent;
      let nextTemp = temp;
      if (temp?.json) {
        const revertedTempJson = applyKrRevertToJson(temp.json);
        if (revertedTempJson !== temp.json) {
          nextTemp = {
            ...temp,
            json: revertedTempJson,
            html: jsonToHtml(revertedTempJson) || '',
          };
        }
      }

      return {
        ...state,
        pageDraftHtmlContent: nextDraft,
        tempGigaReviewContent: nextTemp,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          elements: {
            ...state.gigaKeywordBoost.elements,
            [id]: { ...existing, rejected: true },
          },
        },
      };
    }

    // "Restore GIGA Output" rebuilds the review content from the persisted
    // GIGA draft, which brings every reverted KR rewrite back visually — the
    // user revert flags must come back too, or the popover keeps saying
    // "Reverted" (and Accept keeps running the revert-persist path) for
    // rewrites that are plainly back on screen. `rejected` is only ever set
    // by REJECT_KR_REWRITE (a user action; hydration always writes false),
    // so clearing all of them here exactly undoes the user's reverts.
    case actionTypes.CLEAR_KR_REWRITE_REJECTIONS: {
      const elements = state.gigaKeywordBoost?.elements || {};
      if (!Object.values(elements).some((element: any) => element?.rejected)) return state;
      const nextElements: Record<string, any> = {};
      for (const elementId of Object.keys(elements)) {
        const element = elements[elementId];
        nextElements[elementId] = element?.rejected ? { ...element, rejected: false } : element;
      }
      return {
        ...state,
        gigaKeywordBoost: {
          ...state.gigaKeywordBoost,
          elements: nextElements,
        },
      };
    }

    // ---------------------------------------------------------------------
    // GIGA — Pipeline v2 (CQ + Links + KR unified attribution).
    // Slice is cleared on every new Giga run, hydrated from the backend
    // report on page mount, and appended to as CHECKPOINT_RESCORED events
    // stream in during an active run.
    // ---------------------------------------------------------------------
    case actionTypes.CLEAR_GIGA_PIPELINE_STATE: {
      return {
        ...state,
        gigaPipeline: null,
      };
    }
    case actionTypes.SET_GIGA_PIPELINE_REPORT: {
      const report = action?.payload;
      if (!report) {
        return { ...state, gigaPipeline: null };
      }
      return {
        ...state,
        gigaPipeline: {
          schemaVersion: report.schemaVersion,
          selectedOptions: report.selectedOptions || [],
          workflowSteps: report.workflowSteps || [],
          checkpoints: report.checkpoints || [],
          perFlowContributions: report.perFlowContributions || [],
          flowArtifacts: report.flowArtifacts || null,
          officialScores: report.officialScores || null,
          advisory: report.advisory ?? null,
          pageUrl: report.pageUrl || '',
          krReportKey: report.krReportKey,
          legacy: !!report.legacy,
        },
      };
    }
    case actionTypes.ADD_GIGA_PIPELINE_CHECKPOINT: {
      const p = action?.payload;
      if (!p?.stage) return state;
      const prior = state.gigaPipeline;
      // Initialize the slice on first checkpoint of a run so the reducer
      // doesn't need a separate "pipeline run started" action.
      const basePipeline = prior || {
        selectedOptions: [],
        workflowSteps: [],
        checkpoints: [],
        perFlowContributions: [],
        flowArtifacts: null,
        officialScores: null,
        advisory: null,
        pageUrl: '',
      };
      const nextCheckpoints = basePipeline.checkpoints.filter((c: any) => c.stage !== p.stage);
      nextCheckpoints.push({
        stage: p.stage,
        projected_kas: Number(p.projected_kas) || 0,
        overall_tag_group_score: p.overall_tag_group_score || {},
        per_keyword_tag_group_score: p.per_keyword_tag_group_score || {},
        timestamp: Number(p.timestamp) || Date.now(),
        skipped: !!p.skipped,
      });
      return {
        ...state,
        gigaPipeline: {
          ...basePipeline,
          checkpoints: nextCheckpoints,
        },
      };
    }

    case actionTypes.SET_TEMP_MODIFIED_HTML_CONTENT: {
      const html = action?.html;
      const isString = typeof html === 'string';
      const json = isString ? (html ? htmlToJson(html) : null) : html;
      if (json?.tag?.toUpperCase() === 'BODY') {
        json.attributes = {};
      }
      return {
        ...state,
        tempModifiedHtmlContent: {
          html: isString ? html : html ? jsonToHtml(html) : '',
          json: json,
        },
      };
    }

    case actionTypes.SET_IS_ANY_DIRTY_MODIFICATION_IN_HTML_CONTENT: {
      return {
        ...state,
        isAnyDirtyModificationInHtmlContent: action?.isAnyDirtyModificationInHtmlContent,
      };
    }

    case actionTypes.SET_IS_OUTLINE_OPEN: {
      return {
        ...state,
        isOutlineOpen: action?.isOutlineOpen ?? false,
      };
    }

    case actionTypes.UPDATE_GUTENBERG_BLOCK_CONTENT: {
      const { blockId, updatedContent } = action;
      const currentHtml = state.pageDraftHtmlContent?.html || '';

      if (!blockId || !updatedContent) return state;

      const parser = new DOMParser();
      const doc = parser.parseFromString(currentHtml, 'text/html');
      const blockElement = doc.querySelector(`[data-gutenberg-block="${blockId}"]`);

      if (blockElement) {
        // Parse the updated content
        const updatedDoc = parser.parseFromString(updatedContent, 'text/html');
        const newContent = updatedDoc.body.firstElementChild;

        if (newContent) {
          // Preserve Gutenberg attributes
          const gutenbergAttrs = ['data-gutenberg-block', 'data-block-type', 'data-block-index'];

          gutenbergAttrs.forEach((attr) => {
            const value = blockElement.getAttribute(attr);
            if (value) {
              newContent.setAttribute(attr, value);
            }
          });

          // Replace the block element
          blockElement.parentNode?.replaceChild(newContent, blockElement);

          const updatedHtml = doc.body.innerHTML;
          const json = htmlToJson(updatedHtml);
          const orderdExtractedEleIds: any[] = extractAllElementIds(json) || [];

          return {
            ...state,
            pageDraftHtmlContent: {
              html: updatedHtml,
              json: json,
              orderdExtractedEleIds,
            },
          };
        }
      }

      return state;
    }

    case actionTypes.SET_GUIDED_EDITOR_TYPE: {
      const editorType = action?.value;
      return {
        ...state,
        editorType,
      };
    }

    case actionTypes.SET_CMS_PAGE_ID: {
      const cmsPageId = action?.value;
      return {
        ...state,
        cmsPageId,
      };
    }

    case actionTypes.SET_CMS_PAGES_PATH: {
      return {
        ...state,
        cmsPagesPath: action?.value ?? '',
      };
    }

    case actionTypes.SET_USERS_LIST: {
      if (JSON.stringify(state.usersList) === JSON.stringify(action.usersList)) {
        return state;
      }
      return {
        ...state,
        usersList: action.usersList,
      };
    }

    // Edit lock state management
    case actionTypes.SET_EDIT_LOCK_STATE: {
      return {
        ...state,
        editLockState: action.payload,
      };
    }

    case actionTypes.SET_EDIT_LOCK_REQUESTS: {
      return {
        ...state,
        editLockRequests: action.payload,
      };
    }

    case actionTypes.CLEAR_EDIT_LOCK_STATE: {
      return {
        ...state,
        editLockState: null,
        editLockRequests: [],
      };
    }
    case actionTypes.SET_SHORTCODES: {
      return {
        ...state,
        shortcodes: action.value || [],
      };
    }
    // ═══ Content Ideas ═══
    case actionTypes.SET_CONTENT_IDEAS_TASK_ID: {
      return { ...state, contentIdeasTaskId: action.taskId };
    }
    case actionTypes.SET_CONTENT_IDEAS_STATUS: {
      return { ...state, contentIdeasStatus: action.status };
    }
    case actionTypes.SET_CONTENT_IDEAS_RESULTS: {
      return { ...state, contentIdeasResults: action.results };
    }
    case actionTypes.SET_CONTENT_IDEAS_SUMMARY: {
      return { ...state, contentIdeasSummary: action.summary };
    }
    case actionTypes.SET_CONTENT_IDEAS_SAVED: {
      return { ...state, contentIdeasSaved: action.saved };
    }
    case actionTypes.SET_CONTENT_IDEAS_COMPETITORS: {
      return { ...state, contentIdeasCompetitors: action.competitors };
    }
    case actionTypes.SET_CONTENT_IDEAS_TASK_TYPE: {
      return { ...state, contentIdeasTaskType: action.taskType };
    }
    case actionTypes.SET_CONTENT_IDEAS_PLAN: {
      return { ...state, contentIdeasPlan: action.plan };
    }

    case actionTypes.SET_WP_POST_TYPE: {
      return {
        ...state,
        wpPostType: action.value || 'post',
      };
    }

    // ── Taxonomy Designer ──
    case taxonomyActionTypes.SET_TAXONOMY_SESSION: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          session: action.session,
        },
      };
    }

    case taxonomyActionTypes.CLEAR_TAXONOMY_SESSION: {
      return {
        ...state,
        taxonomyDesigner: {
          session: null,
          messages: [],
          isStreaming: false,
          streamingError: null,
          model: [],
          quality: null,
          versions: [],
          currentVersion: null,
          selectedCategory: null,
          sampleUrls: [],
          samplingInfo: null,
          siteShape: [],
          savedDesign: null,
          classifyPagesLocked: false,
          sessionCost: null,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_MESSAGES: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          // Strip duplicated elicitation cards persisted by pre-dedupe builds
          messages: cancelRunningTaxonomyTools(dedupeHydratedTaxonomyQuestions(action.messages), false),
        },
      };
    }

    case taxonomyActionTypes.ADD_TAXONOMY_MESSAGE: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          messages: [...state.taxonomyDesigner.messages, action.message],
        },
      };
    }

    case taxonomyActionTypes.UPDATE_TAXONOMY_MESSAGE: {
      // Idempotence for replayed elicitations: a remount can re-run the init
      // stream, which re-sends a question the transcript already carries on
      // an earlier message. Updating the question on its own message (e.g.
      // marking it answered) is always allowed. Runtime dedupe matches by ID
      // ONLY — a legitimate re-ask (same prompt, new question id, e.g. after
      // an invalid answer) must still render its QuestionCard; prompt-text
      // matching stays confined to hydration healing (SET_TAXONOMY_MESSAGES),
      // where ids regenerate across sessions.
      const incomingQuestion = action.updates?.question;
      const questionAlreadyElsewhere =
        !!incomingQuestion &&
        !!incomingQuestion.id &&
        state.taxonomyDesigner.messages.some(
          (msg: any) => msg.id !== action.messageId && msg.question?.id === incomingQuestion.id
        );
      const updatedMessages = state.taxonomyDesigner.messages.map((msg: any) => {
        if (msg.id !== action.messageId) return msg;
        const updated = { ...msg };
        if (action.updates?.contentAppend) {
          updated.content = (updated.content || '') + action.updates.contentAppend;
          // Timeline: extend the trailing text segment or open a new one.
          // Only messages created segment-aware participate — initializing
          // lazily on a hydrated legacy message would produce a timeline
          // holding just the post-restore chunks and render a truncated
          // transcript.
          if (Array.isArray(updated.segments)) {
            const segments = [...updated.segments];
            const last = segments[segments.length - 1];
            if (last && last.kind === 'text') {
              segments[segments.length - 1] = { ...last, text: last.text + action.updates.contentAppend };
            } else {
              segments.push({ kind: 'text', text: action.updates.contentAppend });
            }
            updated.segments = segments;
          }
        }
        if (action.updates?.toolUse) {
          const toolUses = [...(updated.toolUses || [])];
          const existingIdx = toolUses.findIndex((t: any) => t.id === action.updates.toolUse.id);
          if (existingIdx >= 0) {
            toolUses[existingIdx] = { ...toolUses[existingIdx], ...action.updates.toolUse };
          } else {
            toolUses.push(action.updates.toolUse);
            // Timeline: a NEW tool joins the trailing tools row or opens
            // one. Status updates take the branch above and leave segments
            // untouched — rows reference tools by id.
            if (Array.isArray(updated.segments)) {
              const segments = [...updated.segments];
              const last = segments[segments.length - 1];
              if (last && last.kind === 'tools') {
                segments[segments.length - 1] = { ...last, toolIds: [...last.toolIds, action.updates.toolUse.id] };
              } else {
                segments.push({ kind: 'tools', toolIds: [action.updates.toolUse.id] });
              }
              updated.segments = segments;
            }
          }
          updated.toolUses = toolUses;
        }
        if (action.updates?.diff) {
          updated.diff = action.updates.diff;
        }
        if (incomingQuestion && !questionAlreadyElsewhere) {
          updated.question = incomingQuestion;
        }
        return updated;
      });
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          messages: updatedMessages,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_STREAMING: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          isStreaming: action.isStreaming,
        },
      };
    }

    case taxonomyActionTypes.CANCEL_TAXONOMY_TOOLS: {
      // Sweep every message, not just the newest: only one stream runs at a
      // time, so any lingering `running` elsewhere is stale by definition.
      // isStreaming is left alone — the abort path already clears it.
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          messages: cancelRunningTaxonomyTools(state.taxonomyDesigner.messages, true),
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_STREAMING_ERROR: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          streamingError: action.error,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_MODEL: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          model: action.model,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SESSION_COST: {
      // Merge, not replace: the stream chunk carries the LLM running totals
      // and the poll carries the durable per-provider breakdown — whichever
      // arrives second must not wipe the other's half.
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          sessionCost: {
            ...(state.taxonomyDesigner as any).sessionCost,
            ...action.sessionCost,
          },
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_QUALITY: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          quality: action.quality,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_VERSIONS: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          versions: action.versions,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_CURRENT_VERSION: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          currentVersion: action.version,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SELECTED_CATEGORY: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          selectedCategory: action.category,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SAMPLE_URLS: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          sampleUrls: action.sampleUrls,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SITE_SHAPE: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          siteShape: action.siteShape,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SAMPLING_INFO: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          samplingInfo: action.samplingInfo,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_PENDING_SAVE: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          pendingSave: action.pendingSave,
        },
      };
    }

    case taxonomyActionTypes.SET_TAXONOMY_SAVED_DESIGN: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          savedDesign: action.savedDesign,
        },
      };
    }

    case taxonomyActionTypes.SET_CLASSIFY_PAGES_LOCKED: {
      return {
        ...state,
        taxonomyDesigner: {
          ...state.taxonomyDesigner,
          classifyPagesLocked: action.locked,
        },
      };
    }

    case taxonomyActionTypes.UNDO_TAXONOMY_ACTION: {
      // TODO: implement undo history tracking
      return state;
    }

    default:
      return state;
  }
};

const combineKeywordsData = (cikDataInput: any[] = [], cpkData: any[] = [], aiData: any[] = []) => {
  interface typeCluster {
    cluster_id: string;
    keyword: string;
    sub_keyword: string;
    is_included: boolean;
    is_cluster_included: boolean;
  }

  // The Aurora cluster list (aiData) is rewritten by every generate, regenerate
  // and edit-mode clone, so once it exists it is the complete set of clusters
  // this page has. A CIK row for a cluster that list does not contain is a
  // leftover of an earlier run: Snowflake is flipped a moment later than Aurora
  // is written, and the read behind it can be served from cache. Merging such
  // rows in resurrected the previous run's clusters, ticked, beside the fresh
  // six; on Bluehost /blog/website-migration-checklist/ the "Generate Insights"
  // popup listed eight targeted clusters, two of them from the run before, and
  // they vanished once the pipeline caught up. A page that has no Aurora list
  // at all (aiData empty) keeps showing its CIK rows, as before.
  const aiClusterIds = new Set(
    (aiData || [])
      .filter((c: typeCluster) => c?.cluster_id && !c?.sub_keyword)
      .map((c: typeCluster) => String(c.cluster_id))
  );
  const cikData = aiClusterIds.size
    ? (cikDataInput || []).filter((c: typeCluster) => c?.cluster_id && aiClusterIds.has(String(c.cluster_id)))
    : cikDataInput || [];

  const cpkProcessedButExcludedIds: string[] = [...cpkData]
    ?.filter((c: typeCluster) => c?.cluster_id && !c?.sub_keyword && !c?.is_included && c?.is_cluster_included)
    ?.map((c) => c.cluster_id) as []; //clusterId = processed but currently not  included

  const updatedCIKData = cikData?.filter((cikD) => !cpkProcessedButExcludedIds?.includes(cikD?.cluster_id)); // updatedCikData = all cik excluding  cpk is_included false

  ////aiCLusterIDs = not included or included false
  const aiClusterIDs: string[] = aiData
    ?.filter((c: typeCluster) => c?.cluster_id && !c?.sub_keyword && !c?.is_included)
    ?.map((c) => c?.cluster_id) as [];

  const updatedData: any[] = [...updatedCIKData]?.map((cik) => {
    const newData = { ...cik };
    if (aiClusterIDs.includes(cik?.cluster_id)) {
      newData.is_included = false;
    }
    return newData;
  });

  const cikClusterIDs: string[] = updatedData
    ?.filter(
      (c: typeCluster) => c?.cluster_id && !c?.sub_keyword && !cpkProcessedButExcludedIds?.includes(c?.cluster_id)
    )
    ?.map((c) => c.cluster_id) as [];

  cpkData.forEach((cpk: typeCluster) => {
    if (
      !cikClusterIDs.includes(cpk?.cluster_id) &&
      cpk?.is_included &&
      !cpkProcessedButExcludedIds?.includes(cpk?.cluster_id)
    ) {
      updatedData.push(cpk);
    }
  });

  aiData.forEach((ai: typeCluster) => {
    if (!cikClusterIDs.includes(ai?.cluster_id) && !cpkProcessedButExcludedIds?.includes(ai?.cluster_id)) {
      updatedData.push(ai);
    }
  });

  const cpkClusterIDs: string[] = [...cpkData]
    ?.filter((c: typeCluster) => c?.cluster_id && !c?.sub_keyword)
    ?.map((c) => c.cluster_id) as [];

  const finalData = updatedData?.map((ud) => {
    const newD = { ...ud };
    if (!ud?.sub_keyword && cpkClusterIDs.includes(ud?.cluster_id)) {
      newD.cluster_source = 'dfs';
      newD.is_cpk_data = true;
    }
    return newD;
  });

  return finalData;
};
